import { Typography } from '@mui/material'
import { darken } from '@mui/material/styles'
import { useTimelineStyles } from '../../../hooks/use-timeline-styles'

/**
 * Unified bookmark component for both phases and tasks
 * Uses PhaseBookmark styling and dimensions as baseline
 * Bookmark extends below anchor with text (only wider if needed)
 */
export const Bookmark = ({
  x,                      // Anchor x position (left edge)
  y,                      // Anchor y position
  anchorWidth,            // Original width (anchor width)
  bookmarkWidth,          // Bookmark width (may extend beyond anchor)
  totalHeight,            // Total height of bar
  style,                  // { icon, color, label }
  duration,               // Duration in days
  isActive,               // Active state (clicked)
  isHovered,              // Hover state (local timeline hover)
  isParentOrgHovered,     // Parent organization hover state
  onHover,                // Hover callback
  onClick,                // Click callback
  itemKey,                // Unique key (phaseKey or taskKey)
  onMouseDown,            // Optional: overrides click with pan (interactive mode)
  isMultiSubmit,          // true when single_completion === false
}) => {
  const { PHASE_BORDER_COLOR } = useTimelineStyles()
  // Bookmark is bottom half of the rect
  const anchorHeight = 30  // Height of icon area (PhaseBookmark standard)
  const bookmarkHeight = totalHeight - anchorHeight  // Text area
  const radius = 6  // Corner radius (PhaseBookmark standard)

  const strokeWidth = isParentOrgHovered ? 1 : 0.25  // Constant stroke (PhaseBookmark standard)
  const fillColor = isParentOrgHovered
    ? darken(style.color, 0.2)
    : isActive
      ? darken(style.color, 0.15)
      : style.color

  return (
    <g
      onMouseEnter={() => onHover(itemKey)}
      onMouseLeave={() => onHover(null)}
      onClick={(e) => {
        e.stopPropagation()
        onClick(isActive ? null : itemKey)
      }}
      onMouseDown={onMouseDown}
      data-interactive={onMouseDown ? 'true' : undefined}
      style={{
        cursor: onMouseDown ? 'move' : 'pointer',
      }}
    >
      {/* Bookmark rect (bottom half with text) - anchor is handled by timeline-phase-layer or timeline-task-layer */}
      <path
        d={
          bookmarkWidth > anchorWidth
            ? // Bookmark extends beyond anchor - add top-right radius
              `
              M${x},${y + anchorHeight}
              L${x + bookmarkWidth - radius},${y + anchorHeight}
              Q${x + bookmarkWidth},${y + anchorHeight} ${x + bookmarkWidth},${y + anchorHeight + radius}
              L${x + bookmarkWidth},${y + anchorHeight + bookmarkHeight - radius}
              Q${x + bookmarkWidth},${y + anchorHeight + bookmarkHeight} ${x + bookmarkWidth - radius},${y + anchorHeight + bookmarkHeight}
              L${x + radius},${y + anchorHeight + bookmarkHeight}
              Q${x},${y + anchorHeight + bookmarkHeight} ${x},${y + anchorHeight + bookmarkHeight - radius}
              L${x},${y + anchorHeight}
            `
            : // Bookmark same width or narrower - no top-right radius
              `
              M${x},${y + anchorHeight}
              L${x + bookmarkWidth},${y + anchorHeight}
              L${x + bookmarkWidth},${y + anchorHeight + bookmarkHeight - radius}
              Q${x + bookmarkWidth},${y + anchorHeight + bookmarkHeight} ${x + bookmarkWidth - radius},${y + anchorHeight + bookmarkHeight}
              L${x + radius},${y + anchorHeight + bookmarkHeight}
              Q${x},${y + anchorHeight + bookmarkHeight} ${x},${y + anchorHeight + bookmarkHeight - radius}
              L${x},${y + anchorHeight}
            `
        }
        fill={fillColor}
        stroke={PHASE_BORDER_COLOR}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
      />

      {/* Cover rect to hide top stroke where bookmark overlaps anchor */}
      <rect
        x={x + strokeWidth}
        y={y + anchorHeight - strokeWidth * 4}
        width={(bookmarkWidth > anchorWidth ? anchorWidth : bookmarkWidth) - strokeWidth * 2}
        height={strokeWidth * 8}
        fill={fillColor}
      />

      {/* Inset white border for multi-submission tasks — U-shape (no top), extends into task rect */}
      {isMultiSubmit && (
        <path
          d={`
            M ${x + 3},${y + 10}
            L ${x + 3},${y + anchorHeight + bookmarkHeight - 8}
            Q ${x + 3},${y + anchorHeight + bookmarkHeight - 3} ${x + 8},${y + anchorHeight + bookmarkHeight - 3}
            L ${x + bookmarkWidth - 8},${y + anchorHeight + bookmarkHeight - 3}
            Q ${x + bookmarkWidth - 3},${y + anchorHeight + bookmarkHeight - 3} ${x + bookmarkWidth - 3},${y + anchorHeight + bookmarkHeight - 8}
            L ${x + bookmarkWidth - 3},${y + 10}
          `}
          fill="none"
          stroke="white"
          strokeWidth={1.5}
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Decorative fillet connector - bridges anchor corner to bookmark with curved edge (visible on hover) */}
      {bookmarkWidth - anchorWidth >= 8 && isHovered && (
        <>
          <path
            d={`
              M${x + anchorWidth - 1},${y + anchorHeight}
              L${x + anchorWidth - 1},${y + anchorHeight - 8}
              L${x + anchorWidth},${y + anchorHeight - 8}
              Q${x + anchorWidth},${y + anchorHeight} ${x + anchorWidth + 8},${y + anchorHeight}
              L${x + anchorWidth + 8},${y + anchorHeight + 1}
              L${x + anchorWidth - 1},${y + anchorHeight + 1}
              Z
            `}
            fill={fillColor}
          />
          <path
            d={`
              M${x + anchorWidth},${y + anchorHeight - 8}
              Q${x + anchorWidth},${y + anchorHeight} ${x + anchorWidth + 8},${y + anchorHeight}
            `}
            fill='none'
            stroke={PHASE_BORDER_COLOR}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        </>
      )}

      {/* Label text */}
      <foreignObject
        x={x + 6}
        y={y + anchorHeight + 6}
        width={bookmarkWidth - 12}
        height={14}
        style={{ pointerEvents: 'none' }}
      >
        <Typography
          sx={{
            fontSize: '9px',
            fontWeight: 800,
            opacity: 0.6,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
          }}
        >
          {style.label.toUpperCase()}
        </Typography>
      </foreignObject>

      {/* Duration text */}
      <foreignObject
        x={x + 6}
        y={y + anchorHeight + 22}
        width={bookmarkWidth - 12}
        height={12}
        style={{ pointerEvents: 'none' }}
      >
        <Typography
          sx={{
            fontSize: '8px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis'
          }}
        >
          {duration} DAYS
        </Typography>
      </foreignObject>
    </g>
  )
}
